import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { PortalLayout } from '../components/PortalLayout';
import { PortalSidebar } from '../components/PortalSidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ScrollReveal } from '../components/ScrollReveal';
import { glossary, officialLinks, externalTools, resourcesSidebarGroups } from '../data/resources';
import { ArrowUpRight } from 'lucide-react';

const Resources = () => (
  <>
    <SEO
      title="DPDPA Glossary & Resources — Key Terms and Official Links"
      description="DPDPA glossary of key terms (Data Fiduciary, Data Principal, SDF, Consent Manager) plus official DPDPA Act and DPDP Rules PDF downloads."
      path="/resources"
    />
    <PortalLayout
      breadcrumb={<Breadcrumbs trail={[{ label: 'Resources' }]} />}
      sidebar={<PortalSidebar heading="Resources" headingHref="/resources" groups={resourcesSidebarGroups} activeItemId="glossary" defaultOpenGroupId="resources" />}
    >
      <div data-testid="resources-page" className="max-w-3xl">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-saffron-600 mb-3">Glossary &amp; Official Links</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-navy leading-tight mb-4">Resources</h1>
        <p className="text-gray-600 leading-relaxed mb-10">Key terms, official Act and Rules texts, and compliance tools built specifically for DPDPA compliance.</p>

        <h2 className="font-serif text-2xl text-navy mb-6" data-testid="resources-glossary-heading">Glossary</h2>
        <div className="border border-gray-200 divide-y divide-gray-200 bg-white mb-14" data-testid="resources-glossary-section">
          {glossary.map((g) => (
            <div key={g.term} className="px-5 py-4" data-testid={`glossary-term-${g.term.replace(/\s/g, '-').toLowerCase()}`}>
              <p className="font-serif text-lg text-navy mb-1">{g.term}</p>
              <p className="text-sm text-gray-500 leading-relaxed">{g.definition}</p>
            </div>
          ))}
        </div>

        <div className="bg-navy rounded-sm p-8 mb-14" data-testid="resources-official-links-section">
          <h2 className="font-serif text-2xl text-white mb-6">Official texts &amp; timelines</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {officialLinks.map((l) =>
              l.internal ? (
                <Link key={l.url} to={l.url} data-testid={`official-link-${l.label.slice(0,20).replace(/\s/g,'-').toLowerCase()}`} className="flex items-center justify-between gap-3 border border-white/10 p-4 text-white/80 hover:border-saffron hover:text-saffron transition-colors text-sm">
                  {l.label} <ArrowUpRight size={14} />
                </Link>
              ) : (
                <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`official-link-${l.label.slice(0,20).replace(/\s/g,'-').toLowerCase()}`} className="flex items-center justify-between gap-3 border border-white/10 p-4 text-white/80 hover:border-saffron hover:text-saffron transition-colors text-sm">
                  {l.label} <ArrowUpRight size={14} />
                </a>
              )
            )}
          </div>
        </div>

        <h2 className="font-serif text-2xl text-navy mb-6">Tools for DPDPA compliance</h2>
        <div className="grid sm:grid-cols-2 gap-5" data-testid="resources-tools-section">
          {externalTools.map((t) => (
            <ScrollReveal key={t.url}>
              <a href={t.url} data-testid={`external-tool-${t.label.slice(0,15).replace(/\s/g,'-').toLowerCase()}`} className="block bg-white border border-gray-200 p-6 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(38,71,150,0.1)] transition-all duration-300">
                <p className="font-serif text-lg text-navy mb-2 flex items-center gap-2">{t.label} <ArrowUpRight size={14} /></p>
                <p className="text-sm text-gray-500 leading-relaxed">{t.description}</p>
              </a>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </PortalLayout>
  </>
);

export default Resources;
